import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import Modal from "@mui/material/Modal";
import { DataTable } from "../../common/dataTable/DataTable";
import { mediaAllmappingsStrings } from "./locale";
import { Close as CloseIcon, Refresh } from "@mui/icons-material";
import { AllmappingsFilters } from "../allmappingsFilters/AllmappingsFilters";
import { initLocalization } from "../../../utils/helpers";
import { AllmappingsCreation } from "../allmappingsCreation/AllmappingsCreation";
import useGetAllMedia from "../../../hooks/media/useGetAllMedia";
import {
  DEFAULT_TABLE_PAGINATION,
  DEFAULT_ALL_MAPPINGS_VIEW_FILTERS,
  DEFAULT_ALL_MEDIA_SORTING,
  FRONTEND_TO_BACKEND_SORT_MAP,
  BACKEND_TO_FRONTEND_SORT_MAP,
} from "../../../constants";

export const MappingKeys = () => {
  initLocalization(mediaAllmappingsStrings);

  const [filtersVersion, setFiltersVersion] = useState(1);
  const [filters, setFilters] = useState(DEFAULT_ALL_MAPPINGS_VIEW_FILTERS);
  const [selectedMedia, setSelectedMedia] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_TABLE_PAGINATION);
  const [sorting, setSorting] = useState(DEFAULT_ALL_MEDIA_SORTING);

  const {
    isLoading: allMediaLoading,
    allMedia,
    getAllMedia,
  } = useGetAllMedia(false);

  const allMediaData = useMemo(() => {
    return allMedia?.data || [];
  }, [allMedia]);

  const fetchData = async (overrides = {}) => {
    const payload = {
      ...pagination,
      sortOption: sorting.sortOption,
      mediaId: filters.mediaId || "",
      mediaLabel: filters.mediaLabel || "",
      dataProviderName: filters.media?.[0] || "",
      displayTypeName: filters.displayMediaType || "",
      publisherName: filters.bisLabel || "",
      publisherId: filters.bisId || "",
      ...overrides,
    };

    await getAllMedia(payload);
  };

  useEffect(() => {
    fetchData({ pageNumber: 1 });
  }, []);

  useEffect(() => {
    fetchData({ pageNumber: 1 });
  }, [filtersVersion]);

  const columns = [
    { field: "mediaId", headerName: mediaAllmappingsStrings.mediaId },
    { field: "mediaName", headerName: mediaAllmappingsStrings.mediaLabel },
    {
      field: "dataProviderName",
      headerName: mediaAllmappingsStrings.DataProvider,
    },
    {
      field: "displayTypeName",
      headerName: mediaAllmappingsStrings.DisplayMediaType,
    },
    {
      field: "mappingCount",
      headerName: mediaAllmappingsStrings.NumberAllMediaMapping,
    },
  ];

  const handleSort = async (columnId, direction) => {
    if (!allMediaData.length) return;

    const sortColumn = FRONTEND_TO_BACKEND_SORT_MAP[columnId] || columnId;

    await fetchData({
      ...pagination,
      pageNumber: 1,
      sortOption: {
        sortColumn,
        direction,
      },
    });

    setPagination({ ...pagination, pageNumber: 0 });
    setSorting({ sortOption: { sortColumn, direction } });
  };

  const handleSearch = async () => {
    await fetchData({ pageNumber: 1 });
    setPagination((prev) => ({ ...prev, pageNumber: 0 }));
  };

  const handleSelectRow = (event, index) => {
    setOpenModal(true);
    const clickedMedia = allMediaData[index];
    setSelectedMedia(clickedMedia);
  };

  const handleChangePage = async (event, newPage) => {
    setPagination((prev) => ({ ...prev, pageNumber: newPage }));
    await fetchData({ pageNumber: newPage + 1 });
  };

  const handleChangeRowsPerPage = async (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    const newPagination = {
      ...pagination,
      pageSize: newPageSize,
      pageNumber: 0,
    };

    setPagination(newPagination);
    await fetchData({ ...newPagination, pageNumber: 1 });
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_ALL_MAPPINGS_VIEW_FILTERS);
    setPagination({ ...DEFAULT_TABLE_PAGINATION });
    setSorting(DEFAULT_ALL_MEDIA_SORTING);
    setFiltersVersion((prev) => prev + 1);
  };

  return (
    <Paper sx={{ padding: "1rem", height: "100%", position: "relative" }}>
      <Grid container direction="column" height="100%">
        <Grid xs={12} container justifyContent="space-between" minHeight="32px">
          <Typography>{mediaAllmappingsStrings.title}</Typography>
          <Grid container>
            <Button
              sx={{ ml: 2 }}
              onClick={handleResetFilters}
              startIcon={<Refresh sx={{ transform: "scaleX(-1)" }} />}
              variant="text"
            >
              {mediaAllmappingsStrings.resetFiltersButton}
            </Button>
          </Grid>
        </Grid>

        <Grid xs={12} sx={{ marginBottom: 2 }}>
          <AllmappingsFilters
            key={filtersVersion}
            filters={filters}
            setFilters={setFilters}
            onSearch={handleSearch}
          />
        </Grid>

        <Grid xs={12} display="flex" flexDirection="column" flexGrow="1">
          <DataTable
            columns={columns}
            rows={allMediaData}
            page={pagination.pageNumber}
            rowsPerPage={pagination.pageSize}
            totalRecords={allMedia.totalRecords}
            onChangePage={handleChangePage}
            onSelectRow={handleSelectRow}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            sortBy={
              BACKEND_TO_FRONTEND_SORT_MAP[sorting.sortOption.sortColumn] ??
              sorting.sortOption.sortColumn
            }
            sortDirection={sorting.sortOption.direction}
            sortableColumns={columns.map((col) => col.field)}
            onSort={handleSort}
          />
        </Grid>
      </Grid>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        sx={{
          "&": { zIndex: (theme) => theme.zIndex.drawer - 1, top: 50 },
          "& .MuiModal-backdrop": { top: 50 },
        }}
      >
        <Grid
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100vw",
            height: "calc(100vh - 50px)",
            bgcolor: "#fff",
          }}
        >
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{ position: "absolute", top: 10, right: 10 }}
          >
            <CloseIcon color="primary" sx={{ width: 20, height: 20 }} />
          </IconButton>
          <AllmappingsCreation selectedMedia={selectedMedia} />
        </Grid>
      </Modal>

      <Backdrop
        open={allMediaLoading}
        sx={{
          position: "absolute",
          color: "#fff",
          borderRadius: 1,
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};
